import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styles from './NotificationDrawer.module.scss';
import { Avatar } from '@fluentui/react-components';
import { EyeRegular, EyeOffRegular } from '@fluentui/react-icons';
import { deleteNotificationRequest, markNotificationReadRequest } from '../../store/actions/general.actions';
import { api as generalService } from '../../store/services/general.service';
import { UnassignedTask, DeadlineTask, DailySummary } from '../../modules/default-svg';
import HoverCard from "../hoverCard/HoverCard";
import { Ellipsis, Trash2 } from 'lucide-react';
import { REDIRECTION_TYPES } from '../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const NotificationCard = ({ item, setIsModalOpen, setNotification }) => {
    const PUBLIC_URL = process.env.PUBLIC_URL;
    const dispatch = useDispatch();
    const [displayTitle, setDisplayTitle] = useState('');
    const [displayNotes, setDisplayNotes] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [hasLongContent, setHasLongContent] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const notesRef = useRef(null);
    const menuRef = useRef(null);
    const cardRef = useRef(null);
    const history = useHistory();
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    const deleteNotification = (e) => {
        e.stopPropagation();
        setShowMenu(false);
        dispatch(deleteNotificationRequest(item.id));
    }

    const toggleNotificationReadStatus = (e) => {
        e.stopPropagation();
        setShowMenu(false);

        dispatch(markNotificationReadRequest({
            payload: {
                "Ids": [item.id],
                "IsRead": !item.isRead
            }
        }));
    }

    const handleExpand = (e) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const toggleMenu = (e) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const getPriorityClass = () => {
        const priority = item?.priority || 0;
        switch (priority) {
            case 2:
                return styles.priorityHigh;
            case 1:
                return styles.priorityModerate;
            default:
                return styles.priorityLow;
        }
    };

    const getNotificationIcon = () => {
        switch (item.notificationType) {
            case 'unassignedTask': return <UnassignedTask />;
            case 'taskDeadline': return <DeadlineTask />;
            case 'dailySummary': return <DailySummary />;
            default: return <UnassignedTask />;
        }
    }

    const getIconColor = () => {
        switch (item.notificationType) {
            case 'unassignedTask': return 'blue';
            case 'taskDeadline': return 'red';
            case 'dailySummary': return 'beige';
            default: return 'blue';
        }
    }

    const timeDifference = (date) => {
        if (date) {
            const now = new Date();
            const givenDate = new Date(date);
            const differenceInMs = now - givenDate;

            const seconds = Math.abs(Math.floor(differenceInMs / 1000));
            const minutes = Math.abs(Math.floor(seconds / 60));
            const hours = Math.abs(Math.floor(minutes / 60));
            const days = Math.abs(Math.floor(hours / 24));

            if (seconds < 60) {
                return `now`;
            } else if (minutes < 60) {
                return `${minutes}m ago`;
            } else if (hours < 24) {
                return `${hours}h ago`;
            } else {
                return `${days}d ago`;
            }
        } else {
            return '';
        }
    }

    const transformLinks = (text, links) => {
        if (!text || text.trim() === "" || !text.includes("<@link:"))
            return text;

        const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
        const transformedString = text.replace(regex, (match, type, id) => {
            let entityName;
            if (links && links?.length > 0) {
                entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
            }
            let linkUrl = "";
            if (type === "Client" || type === "Project" || type === "Scope")
                linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
            else if (type === "Portfolio" || type === "Program")
                linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
            else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" ||  type === "Risk" || type === "Task")
                return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;

            if (linkUrl !== "")
                return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
            else
                return `<span class="text-primary">${entityName}</span>`;
        });
        return transformedString;
    }

    useEffect(() => {
        const handleLinkClick = (e) => {
            const target = e.target;
            // Check if the clicked element has the redirection class
            if (target.classList.contains('notification-redirection-link')) {
                e.preventDefault();
                e.stopPropagation();

                const type = target.getAttribute('data-type');
                const id = target.getAttribute('data-id');

                if (type && id) {
                    generalService.getNotificationLinkRedirectionRequest({ type, id })
                    .then((response) => {
                        if(response){
                            if (setIsModalOpen) {
                                setIsModalOpen(false);
                            }

                            if(response.scopeId)
                                handleScopeRedirection(response.scopeId, id);

                            else if(response.projectId)
                                handleProjectRedirection(response.projectId, type, id);

                        }
                    }).catch((error) => {
                    });
                }
            }
        };

        const cardElement = cardRef.current;
        if (cardElement) {
            cardElement.addEventListener('click', handleLinkClick);
        }

        return () => {
            if (cardElement) {
                cardElement.removeEventListener('click', handleLinkClick);
            }
        };
    }, []);
    const handleScopeRedirection = (scopeId, taskId) => {
        history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
    };

    const handleProjectRedirection = (projectId, type, id) => {
        if (projectId && type && id){
            let redirectionType;
            switch (type) {
                case 'changerequest':
                    redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
                    break;
                case 'communicationplan':
                    redirectionType = REDIRECTION_TYPES.COMMUNICATION;
                    break;
                case 'budget':
                    redirectionType = REDIRECTION_TYPES.BUDGET;
                    break;
                case 'risk':
                    redirectionType = REDIRECTION_TYPES.RISK;
                    break;
                case 'task':
                    redirectionType = REDIRECTION_TYPES.TASK;
                    break;
                default:
                    return;
            }
    
            history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
        }

    };

    useEffect(() => {
        setDisplayTitle(transformLinks(item?.title, item?.links));
        setDisplayNotes(transformLinks(item?.notes, item?.links));
    }, [item?.title, item?.notes]);

    useEffect(() => {
        if (notesRef.current && displayNotes) {
            const lineHeight = parseInt(getComputedStyle(notesRef.current).lineHeight);
            const height = notesRef.current.scrollHeight;
            setHasLongContent(height > lineHeight * 2);
        }
    }, [displayNotes]);

    return (
        <div
            ref={cardRef} 
            className={`${styles.notifyCardWrapper} ${getPriorityClass()} ${expanded ? styles.expanded : ''} ${item?.isRead ? styles.isRead : ''}`}
        >
            <div className={styles.actionMenu} ref={menuRef}>
                <button className={styles.menuTrigger} onClick={toggleMenu}>
                    <Ellipsis size={16} />
                </button>
                {showMenu && (
                    <div className={styles.menuDropdown}>
                        <button className={styles.menuItem} onClick={toggleNotificationReadStatus}>
                            {!item?.isRead ? (
                                <>
                                    <EyeRegular style={{ fontSize: '16px' }} />
                                    <span className={styles.notificationStatusAction}>Mark as read</span>
                                </>
                            ) : (
                                <>
                                    <EyeOffRegular style={{ fontSize: '16px' }} />
                                    <span className={styles.notificationStatusAction}>Mark as unread</span>
                                </>
                            )}
                        </button>
                        <button className={`${styles.menuItem} ${styles.menuItemDanger}`} onClick={deleteNotification}>
                            <Trash2 size={16} />
                            <span>Delete</span>
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.avatarWrapper}>
                {item?.creator ? (
                    <HoverCard
                        HoverCardTrigger={
                            <div>
                                <Avatar
                                    size={28}
                                    color="colorful"
                                    name={item?.creator?.name}
                                    image={{ src: item?.creator?.image || null }}
                                />
                            </div>
                        }
                        AvatarId={item?.creator?.id}
                        AvatarName={item?.creator?.name}
                        AvatarImage={item?.creator?.image}
                        AvatarDesignation={item?.creator?.title}
                        AvatarEmail={item?.creator?.email}
                        AvatarPhone={item?.creator?.phoneNumber}
                        AvatarIsExternal={item?.creator?.isExternal}
                        CanSendMessage={true}
                    />
                ) : (
                    <Avatar size={28} color={getIconColor()} icon={getNotificationIcon()} />
                )}
            </div>

            <div className={styles.notifyCardContent}>
                <div className={styles.titleWrapper}>
                    {displayTitle && (
                        <p className={styles.notificationTitle}>
                            <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
                        </p>
                    )}
                    {displayNotes && (
                        <>
                            <p ref={notesRef} className={styles.notificationNotes}>
                                <span dangerouslySetInnerHTML={{ __html: displayNotes }} />
                            </p>
                            {hasLongContent && (
                                <button className={styles.readMoreButton} onClick={handleExpand}>
                                    {expanded ? (
                                        <>
                                            Show less
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="18 15 12 9 6 15"></polyline>
                                            </svg>
                                        </>
                                    ) : (
                                        <>
                                            Read more
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>

                <div className={styles.notificationActions}>
                    <span className={styles.timestamp}>{timeDifference(item?.dateSent)}</span>
                </div>
            </div>
        </div>
    )
}

export default NotificationCard;